/**
 * Taqat AI Employee Store - Main Application Entry
 *
 * Uses React.lazy() for route-based code splitting on all authenticated /app/* routes.
 * Public routes (home, auth, product pages) remain statically imported for fast initial load.
 * Lazy-loaded routes are wrapped in Suspense with a skeleton loading fallback.
 *
 * @module app
 */

import { lazy, Suspense, useEffect, useState } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { Navigate, Route, BrowserRouter as Router, Routes, useParams } from 'react-router-dom'
import { Toaster } from 'sonner'
import { ConditionalAuthProvider } from './components/auth/conditional-auth-provider'
import { InvitationHandler } from './components/auth/invitation-handler'
import { RequireAdmin } from './components/auth/require-admin'
import { DomainLoadingScreen } from './components/domain-loading-screen'
import { DomainNotFoundPage } from './components/domain-not-found-page'
import { FeatureErrorBoundary } from './components/feature-error-boundary'
import { MainLayout, SessionProviderLayout, SettingsLayout } from './components/layout'
import { AppLayout } from './components/layout/app-layout'
import { CodingLayout } from './components/layout/coding-layout'
import { WorkspaceSettingsDialog } from './components/settings/workspace-settings-dialog'
import { Header } from './components/layout/header'
import { WorkflowBuilderLayout } from './components/layout/workflow-builder-layout'
import { RouteLoadingFallback } from './components/route-loading-fallback'
import { SessionProvider } from './components/auth/session-provider'
import { ScrollToTop } from './components/scroll-to-top'
import { ChatWidgetProvider } from './contexts/chat-widget-context'
import { ThemeProvider } from './contexts/theme-context'
import { useDomainRouting } from './hooks/use-domain-routing'
import { withSentryRouting } from './lib/sentry'
import { PwaShell } from './components/pwa'
import { initializeStores } from './store'
import { useAuthStore } from './store/slices/auth-slice'
import { isAuthenticated } from './utils/auth-storage'

// ─── Static imports: Public routes (loaded eagerly for fast initial paint) ────
import { StandaloneAgentChatPage } from './pages/agent/[agentId]/chat/[id]'
import { DemoPage } from './pages/demo'
import { EmployeeProfilePage } from './pages/employee/[id]'
import { PublicFormPage } from './pages/form/[agentId]/[slug]'
import { ForgotPasswordPage } from './pages/forgot-password'
import { InvitationPage } from './pages/invitation/invitation-page'
import { ApprovePage } from './pages/approve/approve-page'
import { AgentStudioPage } from './pages/product/agent-studio'
import { IntegrationsProductPage } from './pages/product/integrations'
import { KnowledgeBaseProductPage } from './pages/product/knowledge-base'
import { MultiAgentProductPage } from './pages/product/multi-agent'
import { WorkflowBuilderPage } from './pages/product/workflow-builder'
import { ReleaseNoteDetailPage } from './pages/release-notes/[slug]'
import { ReleaseNotesPage } from './pages/release-notes'
import { ResourcesPage } from './pages/resources'
import { TermsPage } from './pages/terms'
import { PrivacyPage } from './pages/privacy'
import { SharedChatPage } from './pages/shared-chat'
import { LinkChannelPage } from './pages/link-channel'
import { SigninPage } from './pages/signin'
import { SsoCallbackPage } from './pages/signin/sso-callback'
import { SignupPage } from './pages/signup'
import { StandaloneTeamChatPage } from './pages/teams/[teamId]/chat/[id]'
import { StandaloneTeamChatRedirectPage } from './pages/teams/[teamId]/chat'
import { UseCaseDetailPage } from './pages/use-cases/[slug]'
import { UseCasesPage } from './pages/use-cases'
import { VerifyOtpPage } from './pages/verify-otp'
import { CreateWorkspacePage } from './pages/workspace'

// ─── Lazy imports: Authenticated /app/* routes (code-split per route) ─────────

// Default app redirect (chat-first shell)
import { DefaultAppRedirect } from './components/layout/default-app-redirect'
const AgentDetailPage_V2 = lazy(() => import('./pages/app/[appId]/agents/[agentId]/detail').then(m => ({ default: m.AgentDetailPage })))

// Agent routes
const AgentsPage = lazy(() => import('./pages/app/agent').then(m => ({ default: m.AgentsPage })))
// Teammate Code (spec 083 D2). App-scoped like the rest of the product, so it
// unwinds with `appId` when the App concept goes away.
const CodingPage = lazy(() => import('./pages/app/coding').then(m => ({ default: m.CodingPage })))
const CodingSessionPage = lazy(() => import('./pages/app/coding/[id]').then(m => ({ default: m.CodingSessionPage })))
const CodingPullRequestsPage = lazy(() => import('./pages/app/coding/pull-requests').then(m => ({ default: m.CodingPullRequestsPage })))
const CodingAutomationsPage = lazy(() => import('./pages/app/coding/automations').then(m => ({ default: m.CodingAutomationsPage })))
const CodingAutomationEditorPage = lazy(() => import('./pages/app/coding/automations/editor').then(m => ({ default: m.CodingAutomationEditorPage })))
const AgentCreatePage = lazy(() => import('./pages/app/agent/create').then(m => ({ default: m.AgentCreatePage })))
const AgentEditorPage = lazy(() => import('./pages/app/agent/[agentId]').then(m => ({ default: m.AgentEditorPage })))
const AgentChatDetailsPage = lazy(() => import('./pages/app/agent/[agentId]/chat/[id]').then(m => ({ default: m.AgentChatDetailsPage })))
const AgentPublishPage = lazy(() => import('./pages/app/agent/[agentId]/publish').then(m => ({ default: m.AgentPublishPage })))
const AgentInterfacesPage = lazy(() => import('./pages/app/agent/[agentId]/interfaces').then(m => ({ default: m.AgentInterfacesPage })))
const InterfaceBuilderPage = lazy(() => import('./pages/app/agent/[agentId]/interfaces/builder').then(m => ({ default: m.InterfaceBuilderPage })))

/**
 * Old `/agents/:agentId/chat` route (no chat id) used to render a standalone
 * landing page. That surface has been folded into the agent detail page,
 * which already has the chat input and allocates a new chat id on submit.
 * Keep the URL working with a redirect so any in-the-wild bookmarks land in
 * the right place instead of 404.
 */
function RedirectToAgentDetail() {
  const { appId, agentId } = useParams<{ appId: string; agentId: string }>()
  return <Navigate to={`/app/${appId}/agents/${agentId}`} replace />
}

/**
 * The Audit Log moved under the new Analytics section. Keep the old
 * `/app/:appId/audit-log` URL working (sidebar bookmarks, deep-links) by
 * redirecting to its new home as a tab. Wrapped in `createAppRoute` like the
 * agent-chat redirect so the AppLayout shell is not torn down on the hop.
 */
function RedirectAuditLog() {
  const { appId } = useParams<{ appId: string }>()
  return <Navigate to={`/app/${appId}/analytics/audit-log`} replace />
}

// Chat routes
const ChatPage = lazy(() => import('./pages/app/chat').then(m => ({ default: m.ChatPage })))
const ChatDetailPage = lazy(() => import('./pages/app/chat/[id]').then(m => ({ default: m.ChatDetailPage })))

// Knowledge base routes
const KnowledgeBasePage = lazy(() => import('./pages/app/knowledge-base').then(m => ({ default: m.KnowledgeBasePage })))
const KnowledgeDetailPage = lazy(() => import('./pages/app/knowledge-base/[knowledgeId]').then(m => ({ default: m.KnowledgeDetailPage })))

// Analytics routes (admin-only) — Audit Log now lives as a tab under Analytics
const AnalyticsOverviewPage = lazy(() => import('./pages/app/analytics/overview').then(m => ({ default: m.AnalyticsOverviewPage })))
const AnalyticsAgentsPage = lazy(() => import('./pages/app/analytics/agents').then(m => ({ default: m.AnalyticsAgentsPage })))
const AnalyticsAgentDetailPage = lazy(() => import('./pages/app/analytics/agent-detail').then(m => ({ default: m.AnalyticsAgentDetailPage })))
const AnalyticsAuditLogPage = lazy(() => import('./pages/app/analytics/audit-log').then(m => ({ default: m.AnalyticsAuditLogPage })))

// Library routes
const LibraryPage = lazy(() => import('./pages/app/library').then(m => ({ default: m.LibraryPage })))

// Marketplace routes
const MarketplacePage = lazy(() => import('./pages/app/marketplace').then(m => ({ default: m.MarketplacePage })))
const MarketplaceCategoryPage = lazy(() => import('./pages/app/marketplace/category/[categorySlug]').then(m => ({ default: m.MarketplaceCategoryPage })))
const MarketplaceDetailPage = lazy(() => import('./pages/app/marketplace/[templateid]').then(m => ({ default: m.MarketplaceDetailPage })))
const TeamTemplateDetailPage = lazy(() => import('./pages/app/marketplace/[templateid]/team'))
const SolutionPackageDetailPage = lazy(() => import('./pages/app/solution-packages/detail').then(m => ({ default: m.SolutionPackageDetailPage })))

// Team routes
const TeamHomePage = lazy(() => import('./pages/app/teams/[teamId]').then(m => ({ default: m.TeamHomePage })))

// Apps page (Figma-inspired teams/apps listing)
const AppsPage = lazy(() => import('./pages/app/apps').then(m => ({ default: m.AppsPage })))
const AppStructurePage = lazy(() => import('./pages/app/[appId]/structure').then(m => ({ default: m.AppStructurePage })))
const AppScheduledJobsPage = lazy(() => import('./pages/app/[appId]/scheduled-jobs').then(m => ({ default: m.AppScheduledJobsPage })))

// Task routes
const TasksPage = lazy(() => import('./pages/app/tasks').then(m => ({ default: m.TasksPage })))
const TaskDetailsPage = lazy(() => import('./pages/app/tasks/[taskId]'))
const CreateTaskPage = lazy(() => import('./pages/app/tasks/create'))

// Projects routes (internal-only module). Route registration is unconditional;
// visibility is gated by the server-driven `projectsEnabled` session flag, which
// controls the sidebar link and the server's /api/projects 404 gate.
const ProjectsPage = lazy(() => import('./pages/app/projects').then(m => ({ default: m.ProjectsPage })))
const ProjectBoardPage = lazy(() => import('./pages/app/projects/[projectId]').then(m => ({ default: m.ProjectBoardPage })))
const ProjectIssueDetailPage = lazy(() => import('./pages/app/projects/[projectId]/issues/[issueId]').then(m => ({ default: m.ProjectIssueDetailPage })))

// Workflow routes
const WorkflowsPage = lazy(() => import('./pages/app/workflows').then(m => ({ default: m.WorkflowsPage })))
const WorkflowBuilderV2 = lazy(() => import('./pages/app/workflows/builder-v2'))
const WorkflowExecutionsPage = lazy(() => import('./pages/app/workflows/[id]/executions').then(m => ({ default: m.WorkflowExecutionsPage })))
const WorkflowExecutionDetailsPage = lazy(() => import('./pages/app/workflows/[id]/executions/[executionId]').then(m => ({ default: m.WorkflowExecutionDetailsPage })))

// Command center & feed
const CommandCenterPage = lazy(() => import('./pages/app/command-center'))
const FeedPage = lazy(() => import('./components/feed/feed-page'))

// Agent Mail — top-level route at /app/:appId/agent-mail (NOT under /settings).
// The page component is also rendered by the Settings dialog directly.
const AgentMailSettingsPage = lazy(() => import('./pages/app/settings/agent-mail').then(m => ({ default: m.AgentMailSettingsPage })))

/** Routes wrapped with Sentry routing instrumentation for performance tracing */
const SentryRoutes = withSentryRouting(Routes)


/**
 * Domain state passed to render prop
 */
interface DomainState {
  isCustomDomain: boolean
  domainAgentId: string | null
  domainTeamId: string | null
}

/**
 * Check if the website is accessed from tmmate.ai origin
 * Used to hide auth routes on the marketing website
 */
const isTmmateOrigin = false

/**
 * Domain Routing Wrapper Component
 * Handles custom domain detection and routing before showing main routes
 * Shows loading screen while checking domain, 404 page for invalid domains
 * Uses render prop pattern to pass domain state for conditional route rendering
 */
function DomainRoutingWrapper({
  children
}: {
  children: (domainState: DomainState) => React.ReactNode
}) {
  const { isCheckingDomain, isCustomDomain, domainNotFound, domainAgentId, domainTeamId } = useDomainRouting()

  // Show loading screen while checking domain
  if (isCheckingDomain) {
    return <DomainLoadingScreen />
  }

  // Show 404 page for custom domains without published agents or teams
  if (isCustomDomain && domainNotFound) {
    return <DomainNotFoundPage />
  }

  return <>{children({ isCustomDomain, domainAgentId, domainTeamId })}</>
}


/**
 * Taqat AI Employee Store Application
 *
 * Main application component that manages routing and layout for the entire application.
 * Handles initialization of Zustand stores and provides different layout wrappers
 * for authenticated and public routes.
 *
 * Features:
 * - Store initialization on app startup
 * - Loading state management
 * - Route-based code splitting with React.lazy() for /app/* routes
 * - Suspense boundaries with skeleton loading fallbacks
 * - Session-based authentication routing
 * - Settings pages with nested layouts
 *
 * Route Structure:
 * - Public routes: Home, Auth pages (signin, signup, etc.) - statically imported
 * - Protected routes: App pages requiring authentication - lazy-loaded
 * - Settings routes: Nested under ConditionalAuthProvider with SettingsLayout - lazy-loaded
 */
function App() {
  /**
   * Application initialization state
   * Tracks whether Zustand stores have been initialized on app startup
   */
  const [isInitialized, setIsInitialized] = useState(false)

  /*
    Teammate Code visibility, from the same server-driven flags the sidebar
    uses. The routes are registered only when it is visible, so hiding the
    feature also makes the URLs unreachable rather than merely unadvertised.
    Defaults to visible so the routes are not torn down before the session
    response arrives.

    Read the same way the sidebar reads it — `useAuthStore` takes no selector,
    so passing one silently yields the whole store (always truthy) and the
    routes would never register.
  */
  const codingHidden = useAuthStore().features?.codingHidden ?? false

  // Initialize all Zustand stores on app startup
  useEffect(() => {
    const initialize = async () => {
      await initializeStores()
      setIsInitialized(true)
    }
    initialize()
  }, [])

  /**
   * Helper function to create authenticated routes with ConditionalAuthProvider.
   * Wraps lazy-loaded children in a Suspense boundary so the loading fallback
   * is shown while the route chunk is being fetched.
   *
   * @param headerTitle - Title to display in the header
   * @param children - React node to render as page content (typically a lazy component)
   * @param useSettingsLayout - Whether to wrap content with SettingsLayout (for settings pages)
   * @returns Authenticated route with proper layout wrappers and Suspense boundary
   */
  const createAuthenticatedRoute = (
    headerTitle: string,
    children: React.ReactNode,
    useSettingsLayout = false
  ) => (
    <ConditionalAuthProvider>
      <SessionProviderLayout headerTitle={headerTitle}>
        <Suspense fallback={<RouteLoadingFallback />}>
          {useSettingsLayout ? (
            <SettingsLayout>
              {children}
            </SettingsLayout>
          ) : (
            children
          )}
        </Suspense>
      </SessionProviderLayout>
    </ConditionalAuthProvider>
  )

  /**
   * Helper for app-scoped routes that use AppLayout + AppProvider sidebar
   */
  const createAppRoute = (
    children: React.ReactNode,
  ) => (
    <ConditionalAuthProvider>
      <AppLayout>
        <Suspense fallback={<RouteLoadingFallback />}>
          {children}
        </Suspense>
      </AppLayout>
    </ConditionalAuthProvider>
  )

  /**
   * Helper for the Coding surface, which swaps the platform sidebar for its own
   * session rail (spec 083 D2). Identical to createAppRoute in every other way.
   */
  const createCodingRoute = (
    children: React.ReactNode,
  ) => (
    <ConditionalAuthProvider>
      <CodingLayout>
        <Suspense fallback={<RouteLoadingFallback />}>
          {children}
        </Suspense>
      </CodingLayout>
    </ConditionalAuthProvider>
  )

  /**
   * Helper function to create public routes with Header layout
   * Wraps page content with public-facing header component
   *
   * @param children - React node to render as page content
   * @returns Public route with header layout
   */
  const createPublicRoute = (children: React.ReactNode) => (
    <div className="min-h-screen text-foreground flex flex-col">
      <Header />
      <main className="flex-1">
        {children}
      </main>
    </div>
  )

  /**
   * Helper function to create standalone routes with ConditionalAuthProvider
   * Used for agent chat routes that don't need the full SessionProviderLayout
   * Handles session, API key, and channel user authentication
   *
   * @param children - React node to render as page content
   * @returns Standalone route with conditional authentication provider
   */
  const createStandaloneRoute = (children: React.ReactNode) => (
    <ConditionalAuthProvider>
      <div className="min-h-screen text-foreground">
        {children}
      </div>
      {/* Workspace settings dialog — chat sidebars in standalone routes
          (e.g., /agent/:agentId/chat/:id) open this via the shared store.
          AppLayout has its own mount for app-scoped routes; this covers
          everything else. */}
      <WorkspaceSettingsDialog />
    </ConditionalAuthProvider>
  )

  // Show loading until app is initialized
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-sm text-muted-foreground">Initializing application...</p>
        </div>
      </div>
    )
  }

  return (
    <HelmetProvider>
      <ThemeProvider defaultTheme="system" storageKey="taqat-theme">
        <ChatWidgetProvider>
          <Router>
            <ScrollToTop />
            <DomainRoutingWrapper>
              {({ isCustomDomain, domainAgentId, domainTeamId }) => (
                <>
                  {/* Agent Chat Mode: Custom domains only show agent-related routes */}
                  {isCustomDomain && domainAgentId ? (
                    <SentryRoutes>
                      {/* Agent chat route with conditional authentication */}
                      <Route path="/agent/:agentId/chat/:id" element={createStandaloneRoute(<StandaloneAgentChatPage />)} />

                      {/* Sign-in must render on custom domains too, so the
                          "Sign in to continue" screen's button has a real
                          destination instead of being swallowed by the
                          catch-all below (which would loop back to chat). */}
                      <Route path="/signin" element={<SigninPage />} />

                      {/* SSO handoff must render on the agent custom domain so
                          the session tokens are stored in THIS origin's
                          localStorage before navigating on to the agent chat. */}
                      <Route path="/signin/sso/callback" element={<SsoCallbackPage />} />

                      {/* Catch-all: Redirect any other route to agent chat */}
                      <Route path="*" element={<Navigate to={`/agent/${domainAgentId}/chat/new`} replace />} />
                    </SentryRoutes>
                  ) : isCustomDomain && domainTeamId ? (
                    <SentryRoutes>
                      {/* Team chat routes */}
                      <Route path="/teams/:teamId/chat/:id" element={createStandaloneRoute(<StandaloneTeamChatPage />)} />
                      <Route path="/teams/:teamId/chat" element={<StandaloneTeamChatRedirectPage />} />

                      {/* Catch-all: Redirect any other route to team chat */}
                      <Route path="*" element={<Navigate to={`/teams/${domainTeamId}/chat`} replace />} />
                    </SentryRoutes>
                  ) : (
                    /* Full App Mode: Main domain shows all routes */
                    <SentryRoutes>
                      <Route path="/" element={
                        <Navigate to={isAuthenticated() ? '/app' : '/signin'} replace />
                      } />
                      <Route path="/signin" element={isTmmateOrigin ? <Navigate to="/" replace /> : <SigninPage />} />
                      <Route path="/signin/sso/callback" element={<SsoCallbackPage />} />
                      <Route path="/signup" element={isTmmateOrigin ? <Navigate to="/" replace /> : <SignupPage />} />
                      <Route path="/invitation/:invitationId" element={<InvitationPage />} />
                      {/* Channel bot "link your account" cards land here. Kept
                          outside the workspace shell so zero-workspace sessions
                          aren't bounced to /workspace/create; the page itself
                          handles signed-out / non-member states. */}
                      <Route path="/link/channel" element={<LinkChannelPage />} />
                      {/* Approvers may have no app account — the link token is
                          the only authorization, so this must stay unauthenticated. */}
                      <Route path="/approve" element={<ApprovePage />} />
                      <Route path="/forgot-password" element={isTmmateOrigin ? <Navigate to="/" replace /> : <ForgotPasswordPage />} />
                      <Route path="/verify-otp" element={isTmmateOrigin ? <Navigate to="/" replace /> : <VerifyOtpPage />} />
                      <Route path="/workspace/create" element={<CreateWorkspacePage />} />
                      <Route path="/resources" element={createPublicRoute(<ResourcesPage />)} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />

                      {/* Public routes with Header/Footer layout */}
                      <Route path="/employee/:id" element={createPublicRoute(<EmployeeProfilePage />)} />
                      <Route path="/product/agent-studio" element={
                        <MainLayout>
                          <AgentStudioPage />
                        </MainLayout>
                      } />
                      <Route path="/product/workflow-builder" element={
                        <MainLayout>
                          <WorkflowBuilderPage />
                        </MainLayout>
                      } />
                      <Route path="/product/knowledge-base" element={
                        <MainLayout>
                          <KnowledgeBaseProductPage />
                        </MainLayout>
                      } />
                      <Route path="/product/integration" element={
                        <MainLayout>
                          <IntegrationsProductPage />
                        </MainLayout>
                      } />
                      <Route path="/product/multi-agent" element={
                        <MainLayout>
                          <MultiAgentProductPage />
                        </MainLayout>
                      } />

                      {/* Demo page */}
                      <Route path="/demo" element={
                        <MainLayout>
                          <DemoPage />
                        </MainLayout>
                      } />

                      {/* Use Cases routes */}
                      <Route path="/use-cases" element={
                        <MainLayout>
                          <UseCasesPage />
                        </MainLayout>
                      } />
                      <Route path="/use-cases/:slug" element={
                        <MainLayout>
                          <UseCaseDetailPage />
                        </MainLayout>
                      } />

                      {/* Public release notes routes - no authentication required, no header/footer */}
                      <Route path="/release-notes" element={
                        <div className="h-screen text-foreground overflow-hidden">
                          <ReleaseNotesPage />
                        </div>
                      } />
                      <Route path="/release-notes/:slug" element={
                        <div className="h-screen text-foreground overflow-hidden">
                          <ReleaseNoteDetailPage />
                        </div>
                      } />

                      {/* Public shared chat route - no authentication required */}
                      <Route path="/share/:shareId" element={<SharedChatPage />} />

                      {/* Public form page - no authentication required */}
                      <Route path="/form/:agentId/:slug" element={<PublicFormPage />} />

                      {/* Standalone agent chat routes - conditional authentication for embed support */}
                      <Route path="/agent/:agentId/chat/:id" element={createStandaloneRoute(<StandaloneAgentChatPage />)} />

                      {/* Standalone team chat routes - conditional authentication for embed support */}
                      <Route path="/teams/:teamId/chat/:id" element={createStandaloneRoute(<StandaloneTeamChatPage />)} />
                      <Route path="/teams/:teamId/chat" element={<StandaloneTeamChatRedirectPage />} />

                      {/* ── Authenticated app routes (lazy-loaded) ────────────────────── */}

                      {/* ── Platform routes (no appId) ──────────────────────────────── */}

                      {/* Default app redirect — auto-navigates to default app's chat */}
                      {/* InvitationHandler intercepts invitation params before DefaultAppRedirect runs */}
                      <Route path="/app" element={
                        <ConditionalAuthProvider>
                          <InvitationHandler>
                            <DefaultAppRedirect />
                          </InvitationHandler>
                        </ConditionalAuthProvider>
                      } />

                      {/* Apps listing (accessible via app switcher "Manage Apps") */}
                      <Route path="/app/apps" element={
                        <ConditionalAuthProvider>
                          <AppLayout>
                            <Suspense fallback={<RouteLoadingFallback />}>
                              <FeatureErrorBoundary featureName="Apps">
                                <AppsPage />
                              </FeatureErrorBoundary>
                            </Suspense>
                          </AppLayout>
                        </ConditionalAuthProvider>
                      } />

                      {/* ── App-scoped routes (/app/:appId/*) ───────────────────────── */}

                      {/* App root — redirect to chat (chat-first shell) */}
                      <Route path="/app/:appId" element={
                        <ConditionalAuthProvider>
                          <AppLayout>
                            <Navigate to="chat" replace />
                          </AppLayout>
                        </ConditionalAuthProvider>
                      } />
                      <Route path="/app/:appId/structure" element={createAppRoute(<FeatureErrorBoundary featureName="Team Structure"><AppStructurePage /></FeatureErrorBoundary>)} />
                      <Route path="/app/:appId/agent-mail" element={createAppRoute(<AgentMailSettingsPage />)} />

                      {/* Coding replaces the platform sidebar with its own session
                          rail (spec 083 D2), so it takes CodingLayout rather than
                          createAppRoute — see components/layout/coding-layout.tsx.

                          Not registered at all when the feature is hidden, so the
                          surface cannot be reached by typing the URL either; the
                          paths fall through to the catch-all like any unknown
                          route. Hiding only the sidebar entry would leave it
                          fully usable to anyone who knows the address. */}
                      {!codingHidden && (
                        <>
                          <Route path="/app/:appId/coding" element={createCodingRoute(<FeatureErrorBoundary featureName="Coding"><CodingPage /></FeatureErrorBoundary>)} />
                          {/* Before `:id` for readability. React Router ranks a
                              static segment above a dynamic one regardless, so
                              "pull-requests" is never mistaken for a session id. */}
                          <Route path="/app/:appId/coding/pull-requests" element={createCodingRoute(<FeatureErrorBoundary featureName="Coding"><CodingPullRequestsPage /></FeatureErrorBoundary>)} />
                          <Route path="/app/:appId/coding/automations" element={createCodingRoute(<FeatureErrorBoundary featureName="Coding"><CodingAutomationsPage /></FeatureErrorBoundary>)} />
                          {/* Static before dynamic so "new" is never read as a rule id. */}
                          <Route path="/app/:appId/coding/automations/new" element={createCodingRoute(<FeatureErrorBoundary featureName="Coding"><CodingAutomationEditorPage /></FeatureErrorBoundary>)} />
                          <Route path="/app/:appId/coding/automations/:ruleId" element={createCodingRoute(<FeatureErrorBoundary featureName="Coding"><CodingAutomationEditorPage /></FeatureErrorBoundary>)} />
                          <Route path="/app/:appId/coding/:id" element={createCodingRoute(<FeatureErrorBoundary featureName="Coding"><CodingSessionPage /></FeatureErrorBoundary>)} />
                        </>
                      )}

                      {/* App-scoped chat */}
                      <Route path="/app/:appId/chat" element={createAppRoute(<FeatureErrorBoundary featureName="Chat"><ChatPage /></FeatureErrorBoundary>)} />
                      <Route path="/app/:appId/chat/:id" element={createAppRoute(<FeatureErrorBoundary featureName="Chat"><ChatDetailPage /></FeatureErrorBoundary>)} />

                      {/* App-scoped Analytics (admin-only) — Overview / Agents / Audit Log tabs.
                          Audit Log keeps a dedicated route so its deep-links keep working; the
                          old `/audit-log` path redirects here. */}
                      <Route path="/app/:appId/analytics" element={createAppRoute(<RequireAdmin fallback="screen"><FeatureErrorBoundary featureName="Analytics"><AnalyticsOverviewPage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/analytics/agents" element={createAppRoute(<RequireAdmin fallback="screen"><FeatureErrorBoundary featureName="Analytics"><AnalyticsAgentsPage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/analytics/agents/:agentId" element={createAppRoute(<RequireAdmin fallback="screen"><FeatureErrorBoundary featureName="Analytics"><AnalyticsAgentDetailPage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/analytics/audit-log" element={createAppRoute(<RequireAdmin fallback="screen"><AnalyticsAuditLogPage /></RequireAdmin>)} />
                      <Route path="/app/:appId/audit-log" element={createAppRoute(<RedirectAuditLog />)} />

                      {/* App-scoped agents — the list, create and edit screens are open to every
                          member: they own the agents they create, publishing included. The API
                          is the authority (`requireAgentWriteAccess` allows admin-or-creator),
                          and the editor renders read-only when the loaded agent reports
                          `canEdit: false`. Publishing runs from the builder's Publish control
                          through the same ownership-gated API; the standalone publish and embed
                          interface routes below stay admin-only (nothing links to them, and they
                          configure workspace-wide distribution). Chat stays open to all roles. */}
                      <Route path="/app/:appId/agents" element={createAppRoute(<AgentsPage />)} />
                      <Route path="/app/:appId/agents/create" element={createAppRoute(<AgentCreatePage />)} />
                      <Route path="/app/:appId/agents/:agentId" element={createAppRoute(<AgentDetailPage_V2 />)} />
                      <Route path="/app/:appId/agents/:agentId/edit" element={createAppRoute(<AgentEditorPage />)} />
                      {/* `/agents/:agentId/chat` (no id) removed — the agent detail page is the
                          entry surface and its chat input allocates a UUID on submit. Redirect
                          any stale bookmarks to the detail page rather than 404.

                          Wrapped in `createAppRoute` (same AppLayout shell as the chat and
                          detail routes) so passing through this redirect does NOT unmount and
                          remount AppLayout. A bare element here made every in-app navigation to
                          `…/agents/:agentId/chat` (the "New chat" header button, agents grid,
                          sidebar DMs, marketplace "open agent") tear down and rebuild the whole
                          shell — a full-page-reload flash that only hit agent chats. */}
                      <Route path="/app/:appId/agents/:agentId/chat" element={createAppRoute(<RedirectToAgentDetail />)} />
                      <Route path="/app/:appId/agents/:agentId/chat/:id" element={createAppRoute(<AgentChatDetailsPage />)} />
                      <Route path="/app/:appId/agents/:agentId/publish" element={createAppRoute(<RequireAdmin><AgentPublishPage /></RequireAdmin>)} />
                      <Route path="/app/:appId/agents/:agentId/interfaces" element={createAppRoute(<RequireAdmin><AgentInterfacesPage /></RequireAdmin>)} />
                      <Route path="/app/:appId/agents/:agentId/interfaces/builder" element={createAppRoute(<RequireAdmin><InterfaceBuilderPage /></RequireAdmin>)} />
                      <Route path="/app/:appId/agents/:agentId/interfaces/builder/:uiId" element={createAppRoute(<RequireAdmin><InterfaceBuilderPage /></RequireAdmin>)} />

                      {/* App-scoped team overview */}
                      <Route path="/app/:appId/team-overview" element={createAppRoute(<FeatureErrorBoundary featureName="Teams"><TeamHomePage /></FeatureErrorBoundary>)} />

                      {/* App-scoped knowledge base — admin only */}
                      <Route path="/app/:appId/knowledge-base" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Knowledge Base"><KnowledgeBasePage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/knowledge-base/:knowledgeId" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Knowledge Base"><KnowledgeDetailPage /></FeatureErrorBoundary></RequireAdmin>)} />

                      {/* App-scoped projects — internal-only Kanban module.
                          Any member may enter; the API only returns projects
                          they've been added to (admins see everything). */}
                      <Route path="/app/:appId/projects" element={createAppRoute(<FeatureErrorBoundary featureName="Projects"><ProjectsPage /></FeatureErrorBoundary>)} />
                      <Route path="/app/:appId/projects/:projectId" element={createAppRoute(<FeatureErrorBoundary featureName="Projects"><ProjectBoardPage /></FeatureErrorBoundary>)} />
                      <Route path="/app/:appId/projects/:projectId/issues/:issueId" element={createAppRoute(<FeatureErrorBoundary featureName="Projects"><ProjectIssueDetailPage /></FeatureErrorBoundary>)} />

                      {/* App-scoped automation — admin only */}
                      <Route path="/app/:appId/automations" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Workflows"><WorkflowsPage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/automations/flows" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Workflows"><WorkflowsPage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/automations/jobs" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Workflows"><WorkflowsPage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/automations/flows/builder-v2" element={
                        <ConditionalAuthProvider>
                          <WorkflowBuilderLayout>
                            <Suspense fallback={<RouteLoadingFallback />}>
                              <FeatureErrorBoundary featureName="Workflows">
                                <WorkflowBuilderV2 />
                              </FeatureErrorBoundary>
                            </Suspense>
                          </WorkflowBuilderLayout>
                        </ConditionalAuthProvider>
                      } />
                      <Route path="/app/:appId/automations/flows/builder-v2/:workflowId" element={
                        <ConditionalAuthProvider>
                          <WorkflowBuilderLayout>
                            <Suspense fallback={<RouteLoadingFallback />}>
                              <FeatureErrorBoundary featureName="Workflows">
                                <WorkflowBuilderV2 />
                              </FeatureErrorBoundary>
                            </Suspense>
                          </WorkflowBuilderLayout>
                        </ConditionalAuthProvider>
                      } />
                      <Route path="/app/:appId/automations/flows/builder-v2/:workflowId/test" element={
                        <ConditionalAuthProvider>
                          <WorkflowBuilderLayout>
                            <Suspense fallback={<RouteLoadingFallback />}>
                              <FeatureErrorBoundary featureName="Workflows">
                                <WorkflowBuilderV2 />
                              </FeatureErrorBoundary>
                            </Suspense>
                          </WorkflowBuilderLayout>
                        </ConditionalAuthProvider>
                      } />
                      <Route path="/app/:appId/automations/flows/:id/executions" element={
                        <ConditionalAuthProvider>
                          <WorkflowBuilderLayout>
                            <Suspense fallback={<RouteLoadingFallback />}>
                              <FeatureErrorBoundary featureName="Workflows">
                                <WorkflowExecutionsPage />
                              </FeatureErrorBoundary>
                            </Suspense>
                          </WorkflowBuilderLayout>
                        </ConditionalAuthProvider>
                      } />
                      <Route path="/app/:appId/automations/flows/:id/executions/:executionId" element={
                        <ConditionalAuthProvider>
                          <WorkflowBuilderLayout>
                            <Suspense fallback={<RouteLoadingFallback />}>
                              <FeatureErrorBoundary featureName="Workflows">
                                <WorkflowExecutionDetailsPage />
                              </FeatureErrorBoundary>
                            </Suspense>
                          </WorkflowBuilderLayout>
                        </ConditionalAuthProvider>
                      } />

                      {/* App-scoped library — workspace-wide artifact browser */}
                      <Route path="/app/:appId/library" element={createAppRoute(<FeatureErrorBoundary featureName="Library"><LibraryPage /></FeatureErrorBoundary>)} />

                      {/* App-scoped scheduled jobs (tasks) */}
                      <Route path="/app/:appId/automations/jobs" element={createAppRoute(<AppScheduledJobsPage />)} />
                      <Route path="/app/:appId/automations/jobs/create" element={createAppRoute(<CreateTaskPage />)} />
                      <Route path="/app/:appId/automations/jobs/:taskId" element={createAppRoute(<TaskDetailsPage />)} />

                      {/* App-scoped marketplace — admin only */}
                      <Route path="/app/:appId/marketplace" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Marketplace"><MarketplacePage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/marketplace/category/:categorySlug" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Marketplace"><MarketplaceCategoryPage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/marketplace/:templateId/team" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Marketplace"><TeamTemplateDetailPage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/marketplace/:templateId" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Marketplace"><MarketplaceDetailPage /></FeatureErrorBoundary></RequireAdmin>)} />
                      <Route path="/app/:appId/marketplace/solutions/:packageId" element={createAppRoute(<RequireAdmin><FeatureErrorBoundary featureName="Marketplace"><SolutionPackageDetailPage /></FeatureErrorBoundary></RequireAdmin>)} />

                      {/* App-scoped command center — admin only */}
                      <Route path="/app/:appId/command-center" element={
                        <ConditionalAuthProvider>
                          <WorkflowBuilderLayout>
                            <Suspense fallback={<RouteLoadingFallback />}>
                              <RequireAdmin><CommandCenterPage /></RequireAdmin>
                            </Suspense>
                          </WorkflowBuilderLayout>
                        </ConditionalAuthProvider>
                      } />

                      {/* App-scoped feed */}
                      <Route path="/app/:appId/feed" element={createAppRoute(<FeedPage />)} />

                      {/* App-scoped teams */}
                      <Route path="/app/:appId/teams/:teamId" element={createAppRoute(<FeatureErrorBoundary featureName="Teams"><TeamHomePage /></FeatureErrorBoundary>)} />

                      {/* App-scoped settings (workspace/platform settings within app context) */}

                      {/* Fallback route for authenticated pages - handles invitation workspace switching */}
                      <Route path="/app/*" element={
                        createAuthenticatedRoute("Welcome", (
                          <InvitationHandler>
                            <div className="flex items-center justify-center h-full">
                              <div className="text-center">
                                <h1 className="text-2xl font-bold mb-4">Welcome to Taqat AI</h1>
                                <p className="text-muted-foreground">Navigate using the sidebar to explore the platform.</p>
                              </div>
                            </div>
                          </InvitationHandler>
                        ))
                      } />
                    </SentryRoutes>
                  )}
                </>
              )}
            </DomainRoutingWrapper>
            <PwaShell />
            <Toaster />
          </Router>
        </ChatWidgetProvider>
      </ThemeProvider>
    </HelmetProvider>
  )
}

export default App
